import React from "react";
import { InvalidEditionBuildResult, InvalidEditionContext, IPersonBadge, IPersonBadgeShortened } from "../../../../../common/types";
import objectUtils from "../../../../../common/utils/objectUtils";
import Box from "@mui/material/Box";
import { FREYA_BADGE_QR_CODE_PARTS_COUNT } from "../../../../../common/constants";
import { LogResult, ReasonCodes } from "common/enums";

export const parseBadgeData = (badgeData: IPersonBadge | IPersonBadgeShortened) => {
  return objectUtils.camelCaseKeys(badgeData);
};

export const renderSplittedRow = (desc: string, className1: string, className2: string) =>
  desc.split("[SPLIT]").map((row: string, key: number) => (
    <Box className={key === 0 ? className1 : className2} key={key}>
      {row}
    </Box>
  ));

export const decodeBadgeQr = (qrContent: string) => {
  const splitted = qrContent.split("|");
  if (splitted.length !== FREYA_BADGE_QR_CODE_PARTS_COUNT) {
    return null;
  }
  const gbsCode = splitted[0] || null;
  const badgeId = splitted[1] || null;
  const badgeVersionMatch = splitted[3] && splitted[3].match(/\d+/);
  const badgeVersion = badgeVersionMatch ? Number(badgeVersionMatch[0]) : null;
  const passphrase = splitted[5] ? splitted[5].substring(splitted[5].length - 16) : null;

  if (!gbsCode || !badgeId || !badgeVersion || !passphrase) {
    return null;
  }

  return {
    gbsCode,
    badgeId,
    badgeVersion,
    passphrase
  };
};

export const decodeShortenedBadge = (badge): IPersonBadge => {
  const normalizedQrContent = typeof badge?.qCc === "string" ? badge.qCc.replaceAll("~", "|") : "";
  const decodedQr = normalizedQrContent ? decodeBadgeQr(normalizedQrContent) : null;

  return {
    id: badge.bI,
    contact: {
      firstName: badge.c.fN,
      lastName: badge.c.lN,
      jobTitle: badge.c.jT
    },
    organisation: {
      displayName: badge.o.dN,
      standIds: badge.o.sI,
      countryCode: badge.o.cC,
      state: badge.o.s,
      city: badge.o.oC
    },
    qrCodeContent: badge.qCc,
    productCode: badge.pC,
    status: badge.s,
    paymentStatus: badge.pS,
    printedDateTime: badge.pDt,
    customerType: badge.cT,
    photoKey: badge.pK,
    eventEditionGbsCode: decodedQr?.gbsCode
  } as unknown as IPersonBadge;
};

export const buildInvalidEventEditionEvent = (
  qrContent: string,
  expectedEventEditionGbsCode: string,
  context: InvalidEditionContext
): InvalidEditionBuildResult => {
  const decoded = decodeBadgeQr(qrContent);
  if (!decoded) return { isMismatch: false };
  const normalizeEventEdition = (s?: string | null) => (s ?? "").trim().toUpperCase();

  const { gbsCode: scannedEventEditionGbsCode, badgeId } = decoded;

  if (normalizeEventEdition(scannedEventEditionGbsCode) !== normalizeEventEdition(expectedEventEditionGbsCode)) {
    return {
      isMismatch: true,
      event: {
        badgeId,
        source: context.source || "Freya",
        result: LogResult.False,
        productCode: context.productCode || "",
        customerType: context.customerType || "",
        reasonCode: ReasonCodes.InvalidEventEdition,
        reasonCodeValues: [
          { key: "scannedEventEditionGbsCode", value: scannedEventEditionGbsCode },
          { key: "expectedEventEditionGbsCode", value: expectedEventEditionGbsCode }
        ]
      }
    };
  }

  return { isMismatch: false };
};

export const buildInvalidEventEditionEventForAllowed = (
  qrContent: string,
  allowedEventEditionGbsCodes: string[],
  context: InvalidEditionContext
): InvalidEditionBuildResult => {
  const decoded = decodeBadgeQr(qrContent);
  if (!decoded) return { isMismatch: false };
  const normalizeEventEdition = (s?: string | null) => (s ?? "").trim().toUpperCase();

  const { gbsCode: scannedEventEditionGbsCode, badgeId } = decoded;
  const allowedNormalized = allowedEventEditionGbsCodes.map(normalizeEventEdition);
  const scannedNormalized = normalizeEventEdition(scannedEventEditionGbsCode);

  if (!allowedNormalized.includes(scannedNormalized)) {
    return {
      isMismatch: true,
      event: {
        badgeId,
        source: context.source || "Freya",
        result: LogResult.False,
        productCode: context.productCode || "",
        customerType: context.customerType || "",
        reasonCode: ReasonCodes.InvalidEventEdition,
        reasonCodeValues: [
          { key: "scannedEventEditionGbsCode", value: scannedEventEditionGbsCode },
          { key: "expectedEventEditionGbsCode", value: allowedEventEditionGbsCodes.join(",") }
        ]
      }
    };
  }

  return { isMismatch: false };
};
